import { RefObject, useCallback, useEffect, useMemo, useRef, useState } from 'react';

import date from '@gem/services/date-service';

import Tasks from 'APP/Tasks';
import { AudioSourceType } from 'APP/constants/app';
import Entities from 'STORE';

interface IPreviewRecordPresenter {
  audioRef: RefObject<HTMLAudioElement>;
  src: string;
  histogram: string;
  isPlaying: boolean;
  time: string;
  progress: number;
  handlePlay(): void;
  handlePause(): void;
  onTimeUpdate(): void;
  onEnded(): void;
  onCanPlay(): void;
  onChangeProgress(progressPercentage: number): void;
}

export function usePreviewRecordPresenter(): IPreviewRecordPresenter {
  const audioRef = useRef<HTMLAudioElement>(null);

  const [isPlaying, setIsPlaying] = useState<boolean>(false);
  const [currentTime, setCurrentTime] = useState<number>(0);
  const [duration, setDuration] = useState<number>(0);
  const [progress, setProgress] = useState<number>(0);
  const [histogram, setHistogram] = useState<string>('');
  const removeAudioSourceRef = useRef<(() => void) | null>(null);
  const playPromise = useRef<Promise<void>>(Promise.resolve());

  const handlePlay = useCallback((): void => {
    playPromise.current = audioRef.current?.play() || Promise.resolve();
  }, []);

  const handlePause = useCallback((): void => {
    playPromise.current.then(() => {
      audioRef.current?.pause();
    });
  }, []);

  const onTimeUpdate = useCallback((): void => {
    setCurrentTime(audioRef.current?.currentTime || 0);

    const progress =
      ((audioRef.current?.currentTime || 0) * 100) / (audioRef.current?.duration || 0);

    setProgress(!isNaN(progress) ? progress / 100 : 0);
  }, []);

  const onEnded = useCallback((): void => {
    removeAudioSource();
    setCurrentTime(0);
    setProgress(0);
  }, []);

  const onCanPlay = useCallback((): void => {
    setCurrentTime(audioRef.current?.currentTime || 0);
    setDuration(audioRef.current?.duration || 0);
  }, []);

  const onChangeProgress = useCallback((progressPercentage: number): void => {
    if (audioRef.current) {
      audioRef.current.currentTime = audioRef.current.duration * progressPercentage;
    }
  }, []);

  const time = useMemo((): string => {
    return date.secondsToString(duration, currentTime);
  }, [duration, currentTime]);

  const removeAudioSource = useCallback((): void => {
    if (removeAudioSourceRef.current) {
      removeAudioSourceRef.current();
      removeAudioSourceRef.current = null;
    }
  }, []);

  useEffect(() => {
    return () => removeAudioSource();
  }, []);

  useEffect(() => {
    if (Entities.InputPanelsStore.activeInputPanel) {
      Entities.InputPanelsStore.activeInputPanel.voice.getAudioHistogram().then(setHistogram);
    }
  }, []);

  useEffect(() => {
    const onPlay = (): void => {
      setIsPlaying(true);
      removeAudioSourceRef.current = Tasks.app.audioSource.setCurrentSource(
        AudioSourceType.Voice,
        handlePause
      );
    };

    const onPause = (): void => {
      setIsPlaying(false);
      removeAudioSource();
    };

    const audio = audioRef.current;
    audio?.addEventListener('play', onPlay);
    audio?.addEventListener('pause', onPause);

    return () => {
      audio?.removeEventListener('play', onPlay);
      audio?.removeEventListener('pause', onPause);
    };
  }, []);

  return {
    audioRef,
    src: Entities.InputPanelsStore.activeInputPanel?.voice?.src || '',
    histogram,
    isPlaying,
    time,
    progress,
    handlePlay,
    handlePause,
    onTimeUpdate,
    onEnded,
    onCanPlay,
    onChangeProgress,
  };
}
